import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getTickets, getClients, getAgents, createTicket, getContacts, updateTicket, getAnalyticsOverview, getTags } from "../api";
import { Plus, Search, RefreshCw, MoreHorizontal, Check, Ticket as TicketIcon, Zap, Pause, AlertCircle, CheckCircle2 } from "lucide-react";
import { StatusBadge, PriorityBadge, SLABadge, SelectBubble, MiniAvatar } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import TagPill from "../components/TagPill";

const STATUSES = ["Open","In Progress","On Hold","Resolved","Closed"];

// ── filter persistence ───────────────────────────────────────────────────────
// The filter bar used to reset to "Active" every time you left the page, so any
// non-default view had to be rebuilt by hand after opening a single ticket. The
// filters now survive navigation and reload, and ONLY the Clear button resets
// them (Juwan, 2026-08-24).
const FILTERS_KEY = "midtc_ticket_filters";
const DEFAULT_FILTERS = {
  status:"Active", priority:"", client_id:"", assigned_to:"", sla_status:"", search:"", tag:"",
};

function loadSavedFilters() {
  try {
    const raw = JSON.parse(localStorage.getItem(FILTERS_KEY) || "null");
    if (!raw || typeof raw !== "object") return null;
    // Copy only known keys, and only strings — a stale entry written by an older
    // build (or hand-edited) can't smuggle unexpected fields into the query.
    const out = { ...DEFAULT_FILTERS };
    for (const k of Object.keys(DEFAULT_FILTERS)) {
      if (typeof raw[k] === "string") out[k] = raw[k];
    }
    return out;
  } catch (e) { return null; }
}

// A real <a> so the browser hands us its native context menu ("Open link in new
// tab"), middle-click, and ctrl/cmd-click for free — a div with onClick offers
// none of that. Plain left-clicks are still routed through the SPA router rather
// than triggering a full page load.
// Inside the desktop app's embed the ticket pages live under /embed.
const ticketBase = () => (window.location.pathname.startsWith("/embed") ? "/embed/tickets" : "/tickets");

function TicketLink({ id, children, style }) {
  const navigate = useNavigate();
  return (
    <a
      href={`${ticketBase()}/${id}`}
      style={{ color:"inherit", textDecoration:"none", ...style }}
      onClick={(e) => {
        e.stopPropagation();                                  // never double-fire the row handler
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;  // let the browser open a tab
        e.preventDefault();
        navigate(`${ticketBase()}/${id}`);
      }}
    >
      {children}
    </a>
  );
}

// Blue "Scheduled for {date/time}" badge shown on tickets that carry a scheduled_at.
function ScheduledBadge({ at }) {
  const label = new Date(at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" });
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:"rgba(37,99,235,0.14)", border:"1px solid rgba(59,130,246,0.45)", color:"#3b82f6", borderRadius:99, padding:"2px 9px", fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>
      🕓 Scheduled for {label}
    </span>
  );
}

const timeAgo = (d) => {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric" });
};

const SLA_OPTS  = [
  { value:"ok",     label:"In SLA",  color:"var(--badge-green)"  },
  { value:"hold",   label:"On Hold", color:"var(--badge-pink)"   },
  { value:"breach", label:"Breach",  color:"var(--badge-red)"    },
];

function QuickEditBubble({ ticket, agents, onSaved }) {
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(null);
  const [saved,  setSaved]  = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const apply = async (e, field, value) => {
    e.stopPropagation();
    setSaving(field);
    try {
      await updateTicket(ticket.id, { [field]: value });
      setSaved(field);
      setTimeout(() => setSaved(null), 1200);
      onSaved(ticket.id, { [field]: value });
    } catch {}
    finally { setSaving(null); }
  };

  const statusMap = {
    "Open":        "var(--badge-blue)",   "In Progress":"var(--badge-amber)",
    "On Hold":     "var(--badge-orange)", "Resolved":   "var(--badge-green)", "Closed":"var(--badge-gray)",
  };

  return (
    <div ref={ref} style={{ position:"relative" }} onClick={e => e.stopPropagation()}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ background: open ? "var(--t-input)" : "transparent", border:"1px solid",
          borderColor: open ? "rgba(148,163,184,0.18)" : "transparent", borderRadius:6,
          padding:"3px 6px", cursor:"pointer", display:"flex", alignItems:"center",
          color:"var(--t-text-faint)", transition:"all .15s" }}
        title="Quick edit"
      >
        <MoreHorizontal size={14} />
      </button>

      {open && (
        <div style={{
          position:"absolute", right:0, top:"calc(100% + 6px)", zIndex:100,
          background:"var(--t-panel)", border:"1px solid var(--t-border-soft)", borderRadius:12,
          boxShadow:"0 8px 32px rgba(0,0,0,0.5)", padding:14, minWidth:210,
        }}>
          {/* Arrow */}
          <div style={{ position:"absolute", top:-5, right:10, width:10, height:10,
            background:"var(--t-panel)", border:"1px solid var(--t-border-soft)", borderRight:"none",
            borderBottom:"none", transform:"rotate(45deg)" }} />

          {/* Status */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"var(--t-text-faint)", textTransform:"uppercase",
              letterSpacing:"0.8px", marginBottom:7 }}>Status</div>
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              {STATUSES.map(s => {
                const active = ticket.status === s;
                return (
                  <button key={s} onClick={e => apply(e, "status", s)}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                      background: active ? "var(--t-input)" : "transparent",
                      border: "1px solid", borderColor: active ? "rgba(148,163,184,0.18)" : "transparent",
                      borderRadius:6, padding:"5px 10px", cursor:"pointer", textAlign:"left",
                      color: active ? statusMap[s] : "#94a3b8", fontSize:12, fontWeight: active ? 600 : 400 }}>
                    <span style={{ display:"flex", alignItems:"center", gap:7 }}>
                      <span style={{ width:7, height:7, borderRadius:"50%",
                        background: active ? statusMap[s] : "#24314a", flexShrink:0 }} />
                      {s}
                    </span>
                    {saving === "status" && active ? (
                      <span style={{ width:10, height:10, border:"1.5px solid #56677e",
                        borderTopColor:"#93c5fd", borderRadius:"50%", display:"inline-block" }} />
                    ) : (saved === "status" && active) ? (
                      <Check size={11} color="#6ee7b7" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div style={{ height:1, background:"rgba(148,163,184,0.11)", margin:"0 0 12px" }} />

          {/* SLA */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"var(--t-text-faint)", textTransform:"uppercase",
              letterSpacing:"0.8px", marginBottom:7 }}>SLA Status</div>
            <div style={{ display:"flex", gap:6 }}>
              {SLA_OPTS.map(opt => {
                const active = ticket.sla_status === opt.value;
                return (
                  <button key={opt.value} onClick={e => apply(e, "sla_status", opt.value)}
                    style={{ flex:1, padding:"6px 4px", borderRadius:7, cursor:"pointer",
                      border:"1px solid", fontSize:11, fontWeight:600,
                      borderColor: active ? opt.color+"66" : "rgba(148,163,184,0.11)",
                      background: active ? opt.color+"18" : "var(--t-input)",
                      color: active ? opt.color : "#56677e",
                      display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>
                    {saving === "sla_status" && active ? (
                      <span style={{ width:9, height:9, border:"1.5px solid #56677e",
                        borderTopColor:opt.color, borderRadius:"50%", display:"inline-block" }} />
                    ) : (saved === "sla_status" && active) ? (
                      <Check size={10} />
                    ) : null}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div style={{ height:1, background:"rgba(148,163,184,0.11)", margin:"0 0 12px" }} />

          {/* Assign To */}
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:"var(--t-text-faint)", textTransform:"uppercase",
              letterSpacing:"0.8px", marginBottom:7 }}>Assign To</div>
            <div style={{ display:"flex", flexDirection:"column", gap:3, maxHeight:160, overflowY:"auto" }}>
              {[{ id: "", first_name: "Unassigned", last_name: "" }, ...agents.filter(a => a.is_active !== false)].map(a => {
                const active = (ticket.assigned_to ?? "") === (a.id ?? "");
                const name = a.id ? `${a.first_name} ${a.last_name}` : "Unassigned";
                return (
                  <button key={a.id ?? "none"} onClick={e => apply(e, "assigned_to", a.id || null)}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                      background: active ? "var(--t-input)" : "transparent",
                      border: "1px solid", borderColor: active ? "rgba(148,163,184,0.18)" : "transparent",
                      borderRadius:6, padding:"5px 10px", cursor:"pointer", textAlign:"left",
                      color: active ? "#c4b5fd" : "#94a3b8", fontSize:12, fontWeight: active ? 600 : 400 }}>
                    <span style={{ display:"flex", alignItems:"center", gap:7 }}>
                      {a.id
                        ? <span style={{ width:18, height:18, borderRadius:"50%", background:"var(--t-card-head)", color:"#60a5fa", fontSize:9, fontWeight:700, display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{a.first_name.charAt(0)}</span>
                        : <span style={{ width:18, height:18, borderRadius:"50%", background:"var(--t-input)", flexShrink:0 }} />
                      }
                      {name}
                    </span>
                    {saving === "assigned_to" && active ? (
                      <span style={{ width:10, height:10, border:"1.5px solid #56677e",
                        borderTopColor:"#c4b5fd", borderRadius:"50%", display:"inline-block" }} />
                    ) : (saved === "assigned_to" && active) ? (
                      <Check size={11} color="#c4b5fd" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The themed primary button (New Ticket, Create, View): accent gradient plus the
// brand's own button voice (weight / case / tracking / shadow).
const primaryBtn = {
  background: "var(--t-accent-grad)", color: "#fff", border: "none",
  borderRadius: "var(--t-radius-sm, 8px)", cursor: "pointer",
  fontFamily: "var(--t-font-btn, inherit)", fontWeight: "var(--t-btn-weight, 700)",
  textTransform: "var(--t-btn-transform, none)", letterSpacing: "var(--t-btn-letter, 0)",
  boxShadow: "var(--t-btn-shadow, var(--t-accent-glow))",
};

const inputStyle = {
  background: "var(--t-input)",
  border: "1px solid var(--t-border-mid)",
  borderRadius: 8,
  padding: "8px 12px",
  color: "var(--t-text-primary)",
  fontSize: 13,
  boxShadow: "var(--t-shadow-panel)",
};

export default function Tickets() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [params] = useSearchParams();
  const [tickets,   setTickets]   = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [clients,   setClients]   = useState([]);
  const [agents,    setAgents]    = useState([]);
  const [showNew,   setShowNew]   = useState(false);
  const [creating,  setCreating]  = useState(false);
  const [created,   setCreated]   = useState(null); // submitted-ticket confirmation
  const [contacts,  setContacts]  = useState([]);
  const [page,      setPage]      = useState(1);
  const [selected,  setSelected]  = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [stats,     setStats]     = useState({});
  const [allTags,   setAllTags]   = useState([]);

  const [filters, setFilters] = useState(() => {
    const base = loadSavedFilters() || DEFAULT_FILTERS;
    // A deep link wins over whatever was last saved — arriving from the dashboard's
    // "SLA breaches" tile must show breaches, not the view you left behind.
    const fromUrl = {};
    if (params.get("status"))     fromUrl.status     = params.get("status");
    if (params.get("sla_status")) fromUrl.sla_status = params.get("sla_status");
    if (params.get("tag"))        fromUrl.tag        = params.get("tag");
    return { ...base, ...fromUrl };
  });

  // Persist on every change so the view is intact after navigating into a ticket
  // and back. Clear resets to DEFAULT_FILTERS, which this then writes back.
  useEffect(() => {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch (e) { /* private mode */ }
  }, [filters]);

  const [newForm, setNewForm] = useState({
    client_id:"", contact_id:"", subject:"", description:"",
    category:"General", priority:"Medium", assigned_to:"", secondary_assigned_to:"", cc_emails:[],
  });
  const [ccInput, setCcInput] = useState("");

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTickets({ ...filters, page, limit:25 });
      setTickets(res.data.tickets);
      setTotal(res.data.total);
      // The conversation page offers previous / next through THIS list, in this order.
      try {
        sessionStorage.setItem("midtc_ticket_nav", JSON.stringify({
          ids: (res.data.tickets || []).map(t => t.id), page, pageSize: 25, total: res.data.total,
        }));
      } catch (e) { /* storage blocked — prev/next just won't show */ }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters, page]);

  const handleQuickSave = useCallback((ticketId, changes) => {
    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, ...changes } : t));
    fetchTickets();
  }, [fetchTickets]);

  // Clicking anywhere in a row still opens the ticket, but ctrl/cmd/shift-click and
  // middle-click open a new tab instead — matching what the subject <a> does natively,
  // so the whole row behaves like a link no matter where you click it.
  const location = useLocation();
  const openTicket = useCallback((e, id) => {
    const url = `${location.pathname.startsWith("/embed") ? "/embed/tickets" : "/tickets"}/${id}`;
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) {
      window.open(url, "_blank", "noopener");
      return;
    }
    navigate(url);
  }, [navigate, location.pathname]);

  const toggleSelect = useCallback((id) => {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }, []);

  const toggleAll = () => {
    setSelected(prev => prev.size === tickets.length ? new Set() : new Set(tickets.map(t => t.id)));
  };

  const bulkApply = async (field, value) => {
    if (!selected.size) return;
    setBulkSaving(true);
    try {
      await Promise.all([...selected].map(id => updateTicket(id, { [field]: value })));
      setSelected(new Set());
      fetchTickets();
    } catch (e) { console.error(e); }
    finally { setBulkSaving(false); }
  };

  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Auto-refresh every 60 seconds so new submissions appear without manual reload
  useEffect(() => {
    const interval = setInterval(() => { fetchTickets(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  useEffect(() => {
    Promise.all([getClients(), getAgents()])
      .then(([c, a]) => { setClients(c.data); setAgents(a.data); })
      .catch(console.error);
  }, []);

  useEffect(() => {
    getAnalyticsOverview()
      .then(r => setStats(r.data.tickets || {}))
      .catch(() => {});
  }, []);

  // Refetched whenever the ticket list changes: the auto-tagger invents new tags as
  // it classifies, so a fixed on-mount fetch would go stale within the session.
  useEffect(() => {
    getTags().then(r => setAllTags(r.data || [])).catch(() => {});
  }, [tickets]);

  useEffect(() => {
    if (newForm.client_id) {
      getContacts(newForm.client_id).then(r => setContacts(r.data)).catch(() => setContacts([]));
    }
  }, [newForm.client_id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (creating) return;                 // guard against double-submit
    // An address typed into CC but never "Added" used to be dropped silently.
    const pendingCc = ccInput.trim().toLowerCase();
    if (pendingCc && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pendingCc)) { alert("The CC address doesn't look right — fix it or clear it, then create the ticket."); return; }
    const form = pendingCc && !newForm.cc_emails.includes(pendingCc) ? { ...newForm, cc_emails: [...newForm.cc_emails, pendingCc] } : newForm;
    setCreating(true);
    try {
      const res         = await createTicket(form);
      const clientName  = clients.find(c => c.id === newForm.client_id)?.company_name || "";
      const contactName = (() => {
        const c = contacts.find(x => x.id === newForm.contact_id);
        return c ? `${c.first_name} ${c.last_name}` : "";
      })();
      setShowNew(false);
      // Confirm the submission explicitly rather than silently jumping to the
      // ticket — an admin needs to see that it actually went in, and with which
      // ticket number, before leaving the page.
      setCreated({
        id:            res.data.id,
        ticket_number: res.data.ticket_number,
        subject:       newForm.subject,
        priority:      newForm.priority,
        scheduled:     !!res.data.scheduled,
        client_name:   clientName,
        contact_name:  contactName,
        cc_emails:     form.cc_emails,
      });
      setNewForm({ client_id:"", contact_id:"", subject:"", description:"", category:"General", priority:"Medium", assigned_to:"", secondary_assigned_to:"", cc_emails:[] });
      setCcInput("");
      fetchTickets();
    } catch (err) { alert(err.response?.data?.error || "Failed to create ticket"); }
    finally { setCreating(false); }
  };

  const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" });
  const totalPages = Math.ceil(total / 25);

  // COUNT(*) comes back from Postgres as strings ("10","2","0"), so a plain +
  // concatenates ("10"+"2"+"0" = "1020") instead of adding. Coerce to Number.
  const activeOpen = Number(stats.open || 0) + Number(stats.in_progress || 0) + Number(stats.on_hold || 0);

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1600 }} className="fade-in">
      {/* Page head */}
      <div style={{ paddingBottom:14, borderBottom:"1px solid var(--t-border-soft)", marginBottom:16, display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap: isMobile ? "wrap" : undefined, gap: isMobile ? 12 : undefined }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.5px" }}>Support Tickets</h1>
          <p style={{ color:"var(--t-text-faint)", fontSize:13, marginTop:3 }}>
            {activeOpen} open &nbsp;·&nbsp; {total} total &nbsp;·&nbsp; last synced just now
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setShowNew(true)} style={{
            ...primaryBtn, padding:"9px 18px", fontSize:13,
            display:"inline-flex", alignItems:"center", gap:7,
          }}>
            <Plus size={15} /> New Ticket
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap: isMobile ? "wrap" : undefined }}>
        {[
          { val: stats.open         || 0, label:"Open",         color:"#60a5fa", Icon: TicketIcon },
          { val: stats.in_progress  || 0, label:"In Progress",  color:"var(--badge-amber)", Icon: Zap },
          { val: stats.on_hold      || 0, label:"On Hold",      color:"#a78bfa", Icon: Pause },
          { val: stats.sla_breaches || 0, label:"SLA Breaches", color:"#f87171", Icon: AlertCircle },
          { val: stats.resolved     || 0, label:"Resolved",     color:"#34d399", Icon: CheckCircle2 },
        ].map(({ val, label, color, Icon }) => (
          <div key={label} style={{
            flex: isMobile ? "1 1 30%" : 1, background:"var(--t-gloss, none), var(--t-panel)",
            border:"1px solid var(--t-border-soft)", borderRadius:12,
            padding:"13px 15px", position:"relative", overflow:"hidden",
            boxShadow:"0 4px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)",
            transition:"all 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.borderColor="var(--t-accent-soft)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.borderColor="var(--t-border-soft)"; }}>
            <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.07),transparent)" }} />
            <div style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", opacity:0.1 }}>
              <Icon size={26} color={color} />
            </div>
            <div style={{ fontSize:26, fontWeight:800, letterSpacing:-1, lineHeight:1, color, position:"relative" }}>{val}</div>
            <div style={{ fontSize:10, color:"var(--t-text-faint)", marginTop:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", position:"relative" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ flex: isMobile ? "1 1 100%" : 1, minWidth: isMobile ? 0 : 200, display:"flex", alignItems:"center", gap:8,
          background:"var(--t-input)", border:"1px solid var(--t-border-mid)",
          borderRadius:8, padding:"0 12px", transition:"all 0.2s",
          boxShadow:"var(--t-shadow-panel)" }}>
          <Search size={14} style={{ color:"var(--t-text-faint)", flexShrink:0 }} />
          <input value={filters.search} onChange={e => setFilters(p => ({ ...p, search:e.target.value }))}
            placeholder="Search tickets, clients, issues…"
            style={{ background:"none", border:"none", color:"var(--t-text-primary)", fontSize:13,
              padding:"9px 0", width:"100%", outline:"none", fontFamily:"inherit" }} />
        </div>
        <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status:e.target.value }))}
          style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">Status: All</option>
          <option value="Active">Active (Open + In Progress + On Hold)</option>
          <option value="Open">Open</option>
          <option value="In Progress">In Progress</option>
          <option value="On Hold">On Hold</option>
          <option value="Resolved">Resolved</option>
          <option value="Closed">Closed</option>
        </select>
        {[
          ["priority", ["","Critical","High","Medium","Low"], "Priority"],
          ["sla_status", ["","ok","breach","hold"], "SLA"],
        ].map(([key, opts, label]) => (
          <select key={key} value={filters[key]} onChange={e => setFilters(p => ({ ...p, [key]:e.target.value }))}
            style={{ ...inputStyle, cursor:"pointer" }}>
            <option value="">{label}: All</option>
            {opts.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <select value={filters.client_id} onChange={e => setFilters(p => ({ ...p, client_id:e.target.value }))}
          style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">Customer: All</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        <select value={filters.assigned_to} onChange={e => setFilters(p => ({ ...p, assigned_to:e.target.value }))}
          style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">Assigned To: All</option>
          <option value="unassigned">Unassigned</option>
          {agents.filter(a => a.is_active !== false).map(a => (
            <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
          ))}
        </select>
        <select value={filters.tag} onChange={e => setFilters(p => ({ ...p, tag:e.target.value }))}
          style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">Tag: All</option>
          {allTags.map(tg => <option key={tg.id || tg.name} value={tg.name}>{tg.name}</option>)}
        </select>
        {Object.values(filters).some(Boolean) && (
          <button onClick={() => setFilters(DEFAULT_FILTERS)}
            style={{ ...inputStyle, color:"#f87171", cursor:"pointer" }}>Clear</button>
        )}
        <button onClick={fetchTickets} style={{
          background:"var(--t-input)", border:"1px solid var(--t-border-mid)",
          color:"var(--t-text-muted)", borderRadius:8,
          display:"inline-flex", alignItems:"center", gap:7, cursor:"pointer",
          padding:"8px 14px", fontSize:13, fontWeight:500, transition:"all 0.15s",
          boxShadow:"var(--t-shadow-panel)",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor="var(--t-accent)"; e.currentTarget.style.color="var(--t-text-primary)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor="var(--t-border-mid)"; e.currentTarget.style.color="var(--t-text-muted)"; }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Table — flat layout per mockup, no panel wrapper */}
      <div>
        {isMobile ? (
        <div style={{ borderTop:"1px solid var(--t-border-soft)" }}>
          {loading && (
            <div style={{ padding:40, textAlign:"center" }}>
              <div style={{ width:24, height:24, border:"2px solid var(--t-accent)", borderTopColor:"transparent", borderRadius:"50%", margin:"0 auto" }} className="animate-spin" />
            </div>
          )}
          {!loading && tickets.length === 0 && (
            <div style={{ padding:40, textAlign:"center", color:"var(--t-text-faint)", fontSize:13 }}>No tickets found matching your filters.</div>
          )}
          {!loading && tickets.map(t => (
            <div key={t.id}
              onClick={e => openTicket(e, t.id)}
              onAuxClick={e => { if (e.button === 1) { e.preventDefault(); openTicket(e, t.id); } }}
              style={{ padding:"12px 2px", borderBottom:"1px solid var(--t-border-soft)", cursor:"pointer",
                background: selected.has(t.id) ? "var(--t-panel-hi)" : "transparent" }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                <div onClick={e => e.stopPropagation()} style={{ paddingTop:2 }}>
                  <SelectBubble checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} />
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <TicketLink id={t.id} style={{ display:"block", fontSize:13, fontWeight:600, color:"var(--t-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {t.subject}
                  </TicketLink>
                  <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:2 }}>
                    <span style={{ fontFamily:"'SF Mono','Fira Code',monospace", fontWeight:600 }}>{t.ticket_number}</span>
                    {t.client_name ? ` · ${t.client_name}` : ""} · {timeAgo(t.last_activity_at || t.created_at)}
                  </div>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <QuickEditBubble ticket={t} agents={agents} onSaved={handleQuickSave} />
                </div>
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:6, marginTop:8, paddingLeft:28 }}>
                <StatusBadge status={t.status} />
                {t.scheduled_at && <ScheduledBadge at={t.scheduled_at} />}
                <PriorityBadge priority={t.priority} />
                <SLABadge status={t.sla_status} />
                {t.assigned_to_name ? (
                  <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11, color:"var(--t-text-muted)" }}>
                    <MiniAvatar name={t.assigned_to_name} size={18}/>
                    {t.assigned_to_name}
                    {t.secondary_assigned_to_name && (
                      <span title={`Secondary: ${t.secondary_assigned_to_name}`} style={{ display:"inline-flex", alignItems:"center", gap:4, color:"var(--t-text-faint)" }}>
                        + <MiniAvatar name={t.secondary_assigned_to_name} size={16}/>
                      </span>
                    )}
                  </span>
                ) : <span style={{ fontSize:11, color:"var(--t-text-faint)" }}>Unassigned</span>}
                {(t.tags || []).map(tg => (
                  <TagPill key={tg.name} name={tg.name} color={tg.color}
                    onClick={name => setFilters(p => ({ ...p, tag:name }))} />
                ))}
              </div>
            </div>
          ))}
        </div>
        ) : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr>
                <th style={{ padding:"10px 10px 10px 4px", width:36, borderBottom:"1px solid var(--t-border-soft)" }}>
                  <SelectBubble
                    checked={tickets.length > 0 && selected.size === tickets.length}
                    indeterminate={selected.size > 0 && selected.size < tickets.length}
                    onChange={toggleAll}
                  />
                </th>
                {["#","Subject","Submitter","Status","Priority","SLA","Assigned","Updated",""].map(h => (
                  <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--t-text-faint)",
                    textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap",
                    borderBottom:"1px solid var(--t-border-soft)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} style={{ padding:40, textAlign:"center" }}>
                  <div style={{ width:24, height:24, border:"2px solid var(--t-accent)", borderTopColor:"transparent", borderRadius:"50%", margin:"0 auto" }} className="animate-spin" />
                </td></tr>
              )}
              {!loading && tickets.length === 0 && (
                <tr><td colSpan={10} style={{ padding:40, textAlign:"center", color:"var(--t-text-faint)", fontSize:13 }}>No tickets found matching your filters.</td></tr>
              )}
              {!loading && tickets.map(t => (
                <tr key={t.id}
                  onClick={e => openTicket(e, t.id)}
                  onAuxClick={e => { if (e.button === 1) { e.preventDefault(); openTicket(e, t.id); } }}
                  style={{ borderBottom:"1px solid var(--t-border-soft)", cursor:"pointer", transition:"all 0.12s",
                    background: selected.has(t.id) ? "var(--t-panel-hi)" : "transparent" }}
                  onMouseEnter={e => { if (!selected.has(t.id)) e.currentTarget.style.background="var(--t-accent-softer)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = selected.has(t.id) ? "var(--t-panel-hi)" : "transparent"; }}>
                  <td style={{ padding:"12px 10px 12px 4px" }} onClick={e => e.stopPropagation()}>
                    <SelectBubble checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} />
                  </td>
                  <td style={{ padding:"12px", color:"var(--t-text-faint)", fontFamily:"'SF Mono','Fira Code',monospace", fontSize:11, fontWeight:600, whiteSpace:"nowrap", verticalAlign:"middle" }}>
                    <TicketLink id={t.id}>{t.ticket_number}</TicketLink>
                  </td>
                  {/* Subject (with client subline) */}
                  <td style={{ padding:"12px", verticalAlign:"middle", maxWidth:280 }}>
                    <TicketLink id={t.id} style={{ display:"block", fontSize:13, fontWeight:600, color:"var(--t-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {t.subject}
                    </TicketLink>
                    {t.client_name && <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:2 }}>{t.client_name}</div>}
                    {!!(t.tags || []).length && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:5 }}>
                        {t.tags.map(tg => (
                          <TagPill key={tg.name} name={tg.name} color={tg.color}
                            onClick={name => setFilters(p => ({ ...p, tag:name }))} />
                        ))}
                      </div>
                    )}
                  </td>
                  {/* Submitter (with email subline) */}
                  <td style={{ padding:"12px", verticalAlign:"middle", whiteSpace:"nowrap" }}>
                    {t.submitter_name ? (
                      <>
                        <div style={{ fontSize:12, color:"var(--t-text-muted)", fontWeight:500 }}>{t.submitter_name}</div>
                        {t.submitter_email && <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:2 }}>{t.submitter_email}</div>}
                      </>
                    ) : <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>—</span>}
                  </td>
                  <td style={{ padding:"12px", verticalAlign:"middle" }}>
                    <StatusBadge status={t.status} />
                    {t.scheduled_at && <div style={{ marginTop:5 }}><ScheduledBadge at={t.scheduled_at} /></div>}
                  </td>
                  <td style={{ padding:"12px", verticalAlign:"middle" }}><PriorityBadge priority={t.priority} /></td>
                  <td style={{ padding:"12px", verticalAlign:"middle" }}><SLABadge status={t.sla_status} /></td>
                  {/* Assigned with mini-avatar */}
                  <td style={{ padding:"12px", verticalAlign:"middle", whiteSpace:"nowrap" }}>
                    {t.assigned_to_name ? (
                      <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, color:"var(--t-text-muted)" }}>
                        <MiniAvatar name={t.assigned_to_name} size={22}/>
                        {t.assigned_to_name}
                        {t.secondary_assigned_to_name && (
                          <span title={`Secondary: ${t.secondary_assigned_to_name}`}>
                            <MiniAvatar name={t.secondary_assigned_to_name} size={18}/>
                          </span>
                        )}
                      </span>
                    ) : <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>Unassigned</span>}
                  </td>
                  <td style={{ padding:"12px", verticalAlign:"middle", whiteSpace:"nowrap" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      {t.last_activity_at && Date.now() - new Date(t.last_activity_at).getTime() < 7200000 && (
                        <span title="Updated in the last 2 hours" style={{ width:7, height:7, borderRadius:"50%", background:"#f97316", flexShrink:0 }} />
                      )}
                      <div>
                        <div style={{ fontSize:12, color:"var(--t-text-muted)", fontWeight:500 }}>{timeAgo(t.last_activity_at || t.created_at)}</div>
                        <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:2 }}>Created {fmt(t.created_at)}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:"12px 8px", textAlign:"right", verticalAlign:"middle" }} onClick={e => e.stopPropagation()}>
                    <QuickEditBubble ticket={t} agents={agents} onSaved={handleQuickSave} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding:"12px 16px", borderTop:"1px solid var(--t-border-soft)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>Page {page} of {totalPages} · {total} tickets</span>
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}
                style={{ ...inputStyle, padding:"4px 12px", cursor:"pointer", opacity:page===1?0.4:1 }}>Prev</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}
                style={{ ...inputStyle, padding:"4px 12px", cursor:"pointer", opacity:page===totalPages?0.4:1 }}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div style={{
          position:"fixed", bottom: isMobile ? 14 : 24,
          left: isMobile ? 14 : "50%", right: isMobile ? 14 : undefined,
          transform: isMobile ? "none" : "translateX(-50%)",
          background:"var(--t-panel)", border:"1px solid rgba(148,163,184,0.18)",
          borderRadius:14, boxShadow:"0 8px 40px rgba(0,0,0,0.6)",
          padding:"12px 18px", display:"flex", alignItems:"center", gap: isMobile ? 10 : 14, zIndex:200,
          whiteSpace: isMobile ? "normal" : "nowrap",
          flexWrap: isMobile ? "wrap" : undefined,
        }}>
          <span style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)" }}>
            {selected.size} ticket{selected.size > 1 ? "s" : ""} selected
          </span>
          <div style={{ width:1, height:22, background:"rgba(148,163,184,0.11)" }} />

          {/* Status */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:11, color:"var(--t-text-faint)", fontWeight:500 }}>Status</span>
            <select
              defaultValue=""
              value=""
              onChange={e => { if (e.target.value) bulkApply("status", e.target.value); }}
              disabled={bulkSaving}
              style={{ background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:7,
                padding:"5px 10px", color:"var(--t-text-body)", fontSize:12, cursor:"pointer" }}
            >
              <option value="" disabled>Set…</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Priority */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:11, color:"var(--t-text-faint)", fontWeight:500 }}>Priority</span>
            <select
              value=""
              onChange={e => { if (e.target.value) bulkApply("priority", e.target.value); }}
              disabled={bulkSaving}
              style={{ background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:7,
                padding:"5px 10px", color:"var(--t-text-body)", fontSize:12, cursor:"pointer" }}
            >
              <option value="" disabled>Set…</option>
              {["Critical","High","Medium","Low"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* SLA */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:11, color:"var(--t-text-faint)", fontWeight:500 }}>SLA</span>
            <select
              value=""
              onChange={e => { if (e.target.value) bulkApply("sla_status", e.target.value); }}
              disabled={bulkSaving}
              style={{ background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:7,
                padding:"5px 10px", color:"var(--t-text-body)", fontSize:12, cursor:"pointer" }}
            >
              <option value="" disabled>Set…</option>
              {SLA_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Assign */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:11, color:"var(--t-text-faint)", fontWeight:500 }}>Assign</span>
            <select
              value=""
              onChange={e => bulkApply("assigned_to", e.target.value === "__unassigned__" ? null : e.target.value)}
              disabled={bulkSaving}
              style={{ background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:7,
                padding:"5px 10px", color:"var(--t-text-body)", fontSize:12, cursor:"pointer" }}
            >
              <option value="" disabled>Set…</option>
              <option value="__unassigned__">Unassigned</option>
              {agents.filter(a => a.is_active !== false).map(a => (
                <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
              ))}
            </select>
          </div>

          <div style={{ width:1, height:22, background:"rgba(148,163,184,0.11)" }} />

          {bulkSaving ? (
            <span style={{ width:16, height:16, border:"2px solid rgba(148,163,184,0.18)", borderTopColor:"#60a5fa",
              borderRadius:"50%", display:"inline-block" }} />
          ) : null}

          <button
            onClick={() => setSelected(new Set())}
            style={{ background:"transparent", border:"1px solid rgba(148,163,184,0.18)", borderRadius:7,
              padding:"5px 10px", color:"var(--t-text-muted)", fontSize:12, cursor:"pointer" }}
          >
            Clear
          </button>
        </div>
      )}

      {/* New Ticket Modal */}
      {showNew && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50, padding: isMobile ? 16 : 20 }}>
          <div style={{ background:"var(--t-panel)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:16, padding: isMobile ? 18 : 28, width:"100%", maxWidth:520, maxHeight: isMobile ? "90vh" : undefined, overflowY: isMobile ? "auto" : undefined }} className="fade-in">
            <h2 style={{ fontSize:17, fontWeight:600, color:"var(--t-text-primary)", marginBottom:20 }}>Create New Ticket</h2>
            <form onSubmit={handleCreate} style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <div>
                  <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Customer *</label>
                  <select value={newForm.client_id} onChange={e => setNewForm(p => ({ ...p, client_id:e.target.value, contact_id:"" }))}
                    required style={{ ...inputStyle, width:"100%", cursor:"pointer" }}>
                    <option value="">Select customer</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Submitter</label>
                  <select value={newForm.contact_id} onChange={e => setNewForm(p => ({ ...p, contact_id:e.target.value }))}
                    style={{ ...inputStyle, width:"100%", cursor:"pointer" }} disabled={!newForm.client_id}>
                    <option value="">Select submitter</option>
                    {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Subject *</label>
                <input value={newForm.subject} onChange={e => setNewForm(p => ({ ...p, subject:e.target.value }))}
                  placeholder="Brief description of the issue" required style={{ ...inputStyle, width:"100%" }} />
              </div>
              <div>
                <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Description *</label>
                <textarea value={newForm.description} onChange={e => setNewForm(p => ({ ...p, description:e.target.value }))}
                  placeholder="Detailed description of the issue..." required rows={4}
                  style={{ ...inputStyle, width:"100%", resize:"vertical" }} />
              </div>
              {/* CC field */}
              <div>
                <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>CC (optional)</label>
                <div style={{ display:"flex", gap:6 }}>
                  <input value={ccInput} onChange={e => setCcInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const em = ccInput.trim().toLowerCase();
                        if (em && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em) && !newForm.cc_emails.includes(em)) {
                          setNewForm(p => ({ ...p, cc_emails:[...p.cc_emails, em] }));
                          setCcInput("");
                        }
                      }
                    }}
                    placeholder="email@example.com — press Enter to add"
                    style={{ ...inputStyle, flex:1 }} />
                  <button type="button"
                    onClick={() => {
                      const em = ccInput.trim().toLowerCase();
                      if (em && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em) && !newForm.cc_emails.includes(em)) {
                        setNewForm(p => ({ ...p, cc_emails:[...p.cc_emails, em] }));
                        setCcInput("");
                      }
                    }}
                    style={{ background:"var(--t-input-hi)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:8,
                      padding:"0 12px", color:"var(--t-text-muted)", cursor:"pointer", whiteSpace:"nowrap", fontSize:13 }}>
                    + Add
                  </button>
                </div>
                {newForm.cc_emails.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6 }}>
                    {newForm.cc_emails.map(e => (
                      <span key={e} style={{ display:"inline-flex", alignItems:"center", gap:4,
                        background:"var(--t-accent-softer)", border:"1px solid var(--t-accent-soft)",
                        borderRadius:99, padding:"2px 8px", fontSize:11, color:"var(--t-text-body)" }}>
                        {e}
                        <button type="button" onClick={() => setNewForm(p => ({ ...p, cc_emails:p.cc_emails.filter(x => x !== e) }))}
                          aria-label={`Remove ${e}`}
                          style={{ background:"none", border:"none", color:"var(--t-text-muted)", cursor:"pointer", padding:0, fontSize:13, lineHeight:1, display:"flex" }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                {[
                  ["priority", "Priority", ["Critical","High","Medium","Low"]],
                  ["category", "Category", ["General","Email","Network","Hardware","Software","Security","Onboarding","Performance","Other"]],
                ].map(([key, label, opts]) => (
                  <div key={key}>
                    <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>{label}</label>
                    <select value={newForm[key]} onChange={e => setNewForm(p => ({ ...p, [key]:e.target.value }))}
                      style={{ ...inputStyle, width:"100%", cursor:"pointer" }}>
                      {opts.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                <div>
                  <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Primary Tech</label>
                  <select value={newForm.assigned_to} onChange={e => setNewForm(p => ({ ...p, assigned_to:e.target.value }))}
                    style={{ ...inputStyle, width:"100%", cursor:"pointer" }}>
                    <option value="">Unassigned</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:"var(--t-text-muted)", display:"block", marginBottom:5 }}>Secondary Tech</label>
                  <select value={newForm.secondary_assigned_to} onChange={e => setNewForm(p => ({ ...p, secondary_assigned_to:e.target.value }))}
                    style={{ ...inputStyle, width:"100%", cursor:"pointer" }}>
                    <option value="">None</option>
                    {agents.filter(a => a.id !== newForm.assigned_to).map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:"flex", gap:10, marginTop:4 }}>
                <button type="submit" style={{
                  ...primaryBtn, flex:1, padding:"10px", fontSize:14,
                }} disabled={creating}>{creating ? "Creating…" : "Create Ticket"}</button>
                <button type="button" onClick={() => setShowNew(false)} style={{
                  flex:1, background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.18)",
                  color:"var(--t-text-muted)", padding:"10px", borderRadius:8, fontSize:14, cursor:"pointer",
                }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Submitted confirmation — explicit proof the ticket went in */}
      {created && (
        <div
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:60, padding: isMobile ? 16 : 20 }}
          onClick={() => setCreated(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background:"var(--t-panel)", border:"1px solid rgba(52,211,153,0.35)", borderRadius:16,
              padding: isMobile ? 22 : 30, width:"100%", maxWidth:460, textAlign:"center",
              boxShadow:"0 20px 60px rgba(0,0,0,0.5)" }}
            className="fade-in"
          >
            <div style={{ width:56, height:56, borderRadius:"50%", margin:"0 auto 16px",
              background:"rgba(52,211,153,0.14)", border:"1px solid rgba(52,211,153,0.4)",
              display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Check size={28} color="#34d399" />
            </div>

            <h2 style={{ fontSize:18, fontWeight:800, color:"var(--t-text-primary)", margin:"0 0 6px" }}>
              {created.scheduled ? "Ticket Scheduled" : "Ticket Submitted"}
            </h2>
            <p style={{ fontSize:13, color:"var(--t-text-faint)", margin:"0 0 18px", lineHeight:1.5 }}>
              {created.scheduled
                ? "It is on hold and will be sent automatically at the scheduled time."
                : "The ticket was created and the notifications have gone out."}
            </p>

            <div style={{ background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:10,
              padding:"12px 14px", textAlign:"left", display:"flex", flexDirection:"column", gap:7, marginBottom:20 }}>
              {[
                ["Ticket",   created.ticket_number || "—", true],
                ["Subject",  created.subject,              false],
                ["Customer", created.client_name,          false],
                ["Submitter",created.contact_name,         false],
                ["Priority", created.priority,             false],
                ["CC",       created.cc_emails?.join(", "),false],
              ].filter(([, v]) => v).map(([label, value, strong]) => (
                <div key={label} style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                  <span style={{ fontSize:11, color:"var(--t-text-label)", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:700, flexShrink:0 }}>{label}</span>
                  <span style={{ fontSize: strong ? 13 : 12, fontWeight: strong ? 800 : 400,
                    color: strong ? "var(--t-accent)" : "var(--t-text-body)", textAlign:"right", wordBreak:"break-word" }}>{value}</span>
                </div>
              ))}
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button
                onClick={() => { const id = created.id; setCreated(null); navigate(`${ticketBase()}/${id}`); }}
                style={{ ...primaryBtn, flex:1, padding:"10px", fontSize:13 }}
              >
                View Ticket
              </button>
              <button
                onClick={() => setCreated(null)}
                style={{ flex:1, background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.18)",
                  color:"var(--t-text-muted)", padding:"10px", borderRadius:8, fontSize:13, cursor:"pointer" }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
